/**
 * Slow-request reporter (RUM).
 *
 * Observes the browser's own network requests via the Resource Timing API and
 * reports same-origin requests whose TTFB (time to first byte) exceeds
 * TTFB_THRESHOLD_MS to the backend. Built to diagnose sporadic multi-second
 * stalls that are invisible in server-side metrics because the origin answers
 * fast and the time is lost in transit (Incident 2026-06-11: suspected HTTP/3
 * stream stalls between browser and Cloudflare).
 *
 * Privacy: reports contain only a parameterised path (UUIDs replaced, query
 * string dropped), timing numbers and the negotiated ALPN protocol — never
 * request or response bodies. User/tenant attribution happens server-side via
 * the session cookie.
 */

const TTFB_THRESHOLD_MS = 2000;
const MAX_REPORTS_PER_PAGE = 30;
const REPORT_PATH = '/api/v1/client-metrics/slow-requests';

/** How long observations are collected before one batched POST goes out. */
const FLUSH_DELAY_MS = 5000;
/** Ship early once this many pile up — mirrors the backend's per-batch cap. */
const MAX_BATCH_SIZE = 20;

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const LONG_NUMBER_SEGMENT_RE = /\/\d{4,}(?=\/|$)/g;

// Long-lived SSE event streams (the `/events/` router: `events/chat` POST +
// `events/stream` EventSource). Their "TTFB" is the LLM's time-to-first-token
// and their "duration" is the whole assistant turn (seen up to 7 min) — both
// expected, not transit stalls. Including them drowned the real signal: ~84% of
// reported "slow requests" were just chat streams. Skip them so the reporter
// measures navigations/XHRs, which is what it was built to diagnose.
const STREAMING_PATH_RE = /\/events\//;

/** How often the clock is checked for a gap that only a sleeping machine leaves. */
const SLEEP_CHECK_MS = 5000;
/** A hidden tab's timers are throttled to about once a minute, so it needs more slack. */
const SLEEP_GAP_VISIBLE_MS = 15_000;
const SLEEP_GAP_HIDDEN_MS = 90_000;
/** The network stays unreliable for a while after waking up. */
const AFTER_SLEEP_WINDOW_MS = 60_000;

export interface SlowRequestPayload {
  path: string;
  ttfbMs: number;
  durationMs: number;
  protocol: string;
  status: number | null;
  startedAt: string;
  effectiveType?: string;
  downlinkMbps?: number;
  rttMs?: number;
  queueMs?: number;
  dnsMs?: number;
  connectMs?: number;
  stalledMs?: number;
  waitMs?: number;
  tabVisible: boolean;
  afterSleep: boolean;
}

type TimingPhases = Required<
  Pick<SlowRequestPayload, 'queueMs' | 'dnsMs' | 'connectMs' | 'stalledMs' | 'waitMs'>
>;

let started = false;
let reportCount = 0;
let pending: SlowRequestPayload[] = [];
let flushTimer: number | null = null;
// Everything below is on the performance.now() clock, the one Resource Timing
// entries use. It can stand still while the machine sleeps, so startedAt is no
// reference for any of this.
let hiddenSince: number | null = null;
let lastHiddenEnd = -1;
let lastSleep: { start: number; end: number } | null = null;
let sleepTimer: number | null = null;

/**
 * Reduce a resource URL to a parameterised same-origin path.
 * Returns null for cross-origin URLs (their timing data is unreliable
 * without Timing-Allow-Origin anyway).
 */
export function sanitizePath(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return parsed.pathname.replace(UUID_RE, '{id}').replace(LONG_NUMBER_SEGMENT_RE, '/{n}');
  } catch {
    return null;
  }
}

/**
 * Split the wait into its stretches, so a stall can be placed. The TTFB alone
 * cannot say whether a request never left the browser or was sent and then lost
 * on the way: on 2026-10-30 Traefik saw a request 42s after the click and that
 * was all we could tell (KAN-1977).
 *
 * connectMs of 0 means a reused connection. A request written to a dead one and
 * retried by the browser shows up as a long queueMs followed by a fresh connect.
 *
 * Null when the browser withholds a stamp or they run out of order: the report
 * still goes out without them, and a nonsense value could fail validation and
 * take the rest of its batch down with it.
 */
function timingPhases(entry: PerformanceResourceTiming): TimingPhases | null {
  const stamps = [
    entry.startTime,
    entry.domainLookupStart,
    entry.domainLookupEnd,
    entry.connectStart,
    entry.connectEnd,
    entry.requestStart,
    entry.responseStart,
  ];
  if (!entry.requestStart || stamps.some((stamp, i) => i > 0 && stamp < stamps[i - 1])) {
    return null;
  }
  return {
    queueMs: Math.round(entry.domainLookupStart - entry.startTime),
    dnsMs: Math.round(entry.domainLookupEnd - entry.domainLookupStart),
    connectMs: Math.round(entry.connectEnd - entry.connectStart),
    stalledMs: Math.round(entry.requestStart - entry.connectEnd),
    waitMs: Math.round(entry.responseStart - entry.requestStart),
  };
}

/** Whether the tab stayed in view while this request waited: a person may have seen it. */
function wasTabVisible(entry: PerformanceResourceTiming): boolean {
  const hiddenNow = hiddenSince !== null && hiddenSince <= entry.responseStart;
  return !hiddenNow && lastHiddenEnd < entry.startTime;
}

/** Whether the machine slept during this request or shortly before it. */
function followedSleep(entry: PerformanceResourceTiming): boolean {
  if (!lastSleep) return false;
  return (
    entry.responseStart >= lastSleep.start &&
    entry.startTime <= lastSleep.end + AFTER_SLEEP_WINDOW_MS
  );
}

/**
 * Build a report payload from a resource timing entry, or null when the
 * entry should not be reported (fast, cross-origin, wrong initiator, or the
 * reporting endpoint itself).
 */
export function buildReport(entry: PerformanceResourceTiming): SlowRequestPayload | null {
  if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return null;
  // responseStart is 0 for cross-origin resources without Timing-Allow-Origin —
  // no TTFB to measure.
  if (!entry.responseStart || entry.responseStart <= entry.startTime) return null;

  const ttfbMs = entry.responseStart - entry.startTime;
  if (ttfbMs < TTFB_THRESHOLD_MS) return null;

  const path = sanitizePath(entry.name);
  if (!path) return null;
  // Never report the reporting endpoint — a slow beacon must not loop.
  if (path.startsWith('/api/v1/client-metrics')) return null;
  // Skip long-lived SSE streams — their timing is the chat turn, not a stall.
  if (STREAMING_PATH_RE.test(path)) return null;

  const payload: SlowRequestPayload = {
    path,
    ttfbMs: Math.round(ttfbMs),
    durationMs: Math.round(entry.duration),
    protocol: entry.nextHopProtocol || '',
    // responseStatus is not yet in every browser's ResourceTiming typing.
    status: (entry as { responseStatus?: number }).responseStatus ?? null,
    startedAt: new Date(performance.timeOrigin + entry.startTime).toISOString(),
    ...timingPhases(entry),
    tabVisible: wasTabVisible(entry),
    afterSleep: followedSleep(entry),
  };

  // Network-quality context (Chromium-only NetworkInformation API).
  const connection = (
    navigator as { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }
  ).connection;
  if (connection) {
    if (connection.effectiveType) payload.effectiveType = connection.effectiveType;
    if (typeof connection.downlink === 'number') payload.downlinkMbps = connection.downlink;
    if (typeof connection.rtt === 'number') payload.rttMs = connection.rtt;
  }

  return payload;
}

function flush(): void {
  if (pending.length === 0) return;
  const reports = pending.splice(0, pending.length);
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  // Deliberately NOT fetchWithAuth: a background reporter must never trigger
  // the 401 login redirect. Cookies carry the auth; errors are swallowed.
  void fetch(REPORT_PATH, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reports }),
  }).catch(() => {
    // Fire-and-forget — losing a report is fine, breaking the app is not.
  });

  if (process.env.NODE_ENV !== 'production') {
    console.info('[SlowRequestReporter]', reports);
  }
}

function handleEntry(entry: PerformanceResourceTiming): void {
  if (reportCount >= MAX_REPORTS_PER_PAGE) return;
  const payload = buildReport(entry);
  if (!payload) return;
  reportCount += 1;

  // Collected rather than sent: observations arrive in bursts, because
  // whatever made one request slow usually made its neighbours slow too. One
  // report per request meant the backend got a burst of authenticated
  // requests at the exact moment it was already struggling, which is how a
  // two-minute stall fed itself on 14.08.2026 (KAN-376).
  pending.push(payload);
  if (pending.length >= MAX_BATCH_SIZE) {
    flush();
    return;
  }
  if (flushTimer === null) {
    flushTimer = window.setTimeout(flush, FLUSH_DELAY_MS);
  }
}

function trackVisibility(): void {
  if (document.visibilityState === 'hidden') hiddenSince = performance.now();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince = performance.now();
      flush();
    } else if (hiddenSince !== null) {
      hiddenSince = null;
      lastHiddenEnd = performance.now();
    }
  });
}

/**
 * Notice the machine sleeping: a timer that fires far too late. The gap is read
 * from the wall clock, which keeps running through sleep. A tab frozen by the
 * browser looks the same, and nobody is waiting on that one either.
 */
function trackSleep(): void {
  let lastWall = Date.now();
  let lastPerf = performance.now();
  sleepTimer = window.setInterval(() => {
    const wall = Date.now();
    const perf = performance.now();
    const wasHidden = hiddenSince !== null || lastHiddenEnd >= lastPerf;
    if (wall - lastWall > (wasHidden ? SLEEP_GAP_HIDDEN_MS : SLEEP_GAP_VISIBLE_MS)) {
      lastSleep = { start: lastPerf, end: perf };
    }
    lastWall = wall;
    lastPerf = perf;
  }, SLEEP_CHECK_MS);
}

/**
 * Start observing resource timings. Idempotent — safe to call from a
 * provider that may re-mount; only the first call attaches the observer.
 */
export function initSlowRequestReporter(): void {
  if (started || typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') {
    return;
  }
  started = true;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        handleEntry(entry as PerformanceResourceTiming);
      }
    });
    // buffered: also catch slow requests that finished before the observer
    // was attached (e.g. during initial page load).
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    // Resource timing unsupported — reporter stays inactive.
  }

  // A tab that goes away must not take its collected reports with it. The
  // request carries keepalive, so the browser finishes it after the page is
  // gone. `pagehide` rather than `beforeunload`: it also fires when a mobile
  // browser freezes the tab, which is where reports were being lost.
  window.addEventListener('pagehide', flush);
  trackVisibility();
  trackSleep();
}

/** Test-only: reset module state between test cases. */
export function _resetForTests(): void {
  started = false;
  reportCount = 0;
  pending = [];
  hiddenSince = null;
  lastHiddenEnd = -1;
  lastSleep = null;
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (sleepTimer !== null) {
    clearInterval(sleepTimer);
    sleepTimer = null;
  }
}
